import {
  createCatalogQueries,
  createCatalogQueriesBrands,
  getCategoriesAndFilters,
  getCollectionUrlInfo,
} from '@/utils/Catalog';
import { useRouter } from 'next/router';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setLoading } from '@/store/actions/loadingOverlay';

import {
  FiltersPanel,
  FiltersPanelMobile,
  FiltersPanelMobileBrand,
} from '@/components/organisms';
import { useMediaQuery, useTheme } from '@material-ui/core';
import { ICollectionFiltersCmp } from './ICollectionFiltersCmp';
import { RootState } from '@/store/store';
import { useAppContext } from '@/context/state';
import { useSWRResult } from '@/hooks/useSwrResult';
import { safeFetchAll } from '@/utils/typings/app-fetch';
import { SearchResultResponse } from '@/types/catalog';
import { mapResult } from '@/utils/typings/result';
import { mapSearchResult } from '@/lib/models/mappers/product';

const CollectionFilters: React.FC<ICollectionFiltersCmp> = ({
  serverFilters,
  serverCategories,
  filtersQuery,
  cmsCollectionTags,
  currency,
  type,
}) => {
  const context = useAppContext();
  const settings = context?.settings;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const dispatch = useDispatch();
  const router = useRouter();

  const [currentQuery, setCurrentQuery] = useState<string | null>(null);

  let categories = null;
  let filters = null;

  //from redux
  const segmentPrice = useSelector(
    (state: RootState) => state.userReducer?.customer?.segmentPrice?.value
  );
  const loadingFilters = useSelector(
    (state: RootState) => state.loadingOverlayReducer.filters
  );
  const isLogged = useSelector(
    (state: RootState) => state.userReducer.isLogged
  );

  const {
    slug,
    queryParameters: validQueryParams,
    collapseBy,
  } = getCollectionUrlInfo(router.query, router.asPath, settings || undefined);

  let catalogQueries: {
    [key: string]: string;
  } | null = null;

  if (!filtersQuery) {
    categories = serverCategories;
    filters = serverFilters;
  } else {
    if (filtersQuery !== currentQuery) {
      setCurrentQuery(filtersQuery);
    }

    if (currency?.isoCode && settings) {
      const { sort_by, ...newQueryParams } = validQueryParams;
      if (type === 'brands') {
        catalogQueries = createCatalogQueriesBrands(
          slug[0],
          newQueryParams,
          cmsCollectionTags,
          0,
          250,
          currency?.isoCode,
          currentQuery || undefined,
          segmentPrice,
          collapseBy
        );
      } else {
        catalogQueries = createCatalogQueries(
          router.query,
          router.asPath,
          cmsCollectionTags,
          currency.isoCode,
          settings,
          segmentPrice,
          currentQuery || undefined
        );
      }
    }
  }

  const querykey = catalogQueries ? Object.keys(catalogQueries) : null;

  const { data, error } = useSWRResult(querykey, (...urls: string[]) => {
    return safeFetchAll<SearchResultResponse>(urls).then(results => {
      return mapResult(results, result =>
        result.map((result, index) => ({
          url: urls[index],
          result: mapSearchResult(result),
        }))
      );
    });
  });

  const filterSelectionMobileHandler = (selectedQueryParams: {
    //TODO: this could be probably refactored
    [key: string]: string | string[] | undefined;
  }): void => {
    if (type === 'brands') {
      catalogQueries = createCatalogQueriesBrands(
        slug[0],
        selectedQueryParams,
        cmsCollectionTags,
        0,
        250,
        currency?.isoCode,
        undefined,
        segmentPrice,
        collapseBy
      );
    } else {
      if (currency?.isoCode && settings) {
        const slug = router.query.slug;
        const newRouterQuery = {
          slug,
          ...selectedQueryParams,
        };

        router.push(
          {
            pathname: '/collections/[[...slug]]',
            query: newRouterQuery,
          },
          undefined,
          { shallow: true }
        );
      }
    }
  };

  if (catalogQueries && data) {
    const toShow = getCategoriesAndFilters(
      slug,
      catalogQueries,
      data,
      cmsCollectionTags,
      currency?.isoCode
    );
    if (toShow?.filters) {
      filters = toShow?.filters;
    }
    if (toShow?.categories) {
      categories = toShow?.categories;
    }
  }

  if (filters && type === 'brands') {
    delete filters.artisan;
  } else if (filters) {
    delete filters.season; //TODO: remove after all products have a season
  }

  useEffect(() => {
    if (currentQuery && !isMobile) {
      dispatch(setLoading({ filters: true }));
    }
  }, [currentQuery]);

  useEffect(() => {
    if (data || error) {
      dispatch(setLoading({ filters: false }));
    }
  }, [data, error]);

  return isLogged ? (
    <>
      {isMobile ? (
        type === 'brands' ? (
          <FiltersPanelMobileBrand
            cmsCollectionTags={cmsCollectionTags}
            filters={filters}
            categoryPath={categories}
            filterSelectionMobileHandler={filterSelectionMobileHandler}
            type={type}></FiltersPanelMobileBrand>
        ) : (
          <FiltersPanelMobile
            cmsCollectionTags={cmsCollectionTags}
            filters={filters}
            categoryPath={categories}
            filterSelectionMobileHandler={filterSelectionMobileHandler}
            type={type}></FiltersPanelMobile>
        )
      ) : (
        <FiltersPanel
          cmsCollectionTags={cmsCollectionTags}
          filters={filters}
          categoryPath={categories}
          type={type}
          isLoading={loadingFilters}></FiltersPanel>
      )}
    </>
  ) : (
    <></>
  );
};

export default CollectionFilters;
